import { BodyMedium, BoxSmallLabel, Checkbox, HeadingSmall, theme } from '@arnold/common';
import { getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import {
  OrganizationQuery,
  SurveyGroupRespondentImportInput,
  SurveyGroupRespondentImportType,
  SurveyGroupWithoutRespondentsQuery,
  SurveyStatus,
  useActiveRespondentsQuery,
  UserQuery,
  useSurveyGroupWithRespondentsQuery,
} from '../../generated/hooks';
import { sortRespondents } from '../../lib/common';
import DateInput from '../Common/DateInput';
import { SurveyRespondentsTable } from '../Forms/SurveyParticipantsTable';
import { LoadingCard } from '../LoadingCard';
import { DateSelector, DateSelectorInputGroup } from '../StyledComponents';
import { getDataSource, SurveyGroupSurveysPreviewTable } from './SurveyGroupSurveysPreviewTable';
import TeamSelect from './TeamSelect';

type NewParticipantsFormProps = {
  surveyGroup: NonNullable<SurveyGroupWithoutRespondentsQuery['surveyGroup']>;
  selectedRespondents: SurveyGroupRespondentImportInput[];
  setSelectedRespondents: (
    changeFn: (respondents: SurveyGroupRespondentImportInput[]) => SurveyGroupRespondentImportInput[],
  ) => void;
  organization: UserQuery['user']['organization'] | OrganizationQuery['organization'];
  addedOn: Date | null;
  setAddedOn: (date: Date | null) => void;
  runOngoingSteps: string[];
  onOngoingPhaseChange: (actualStepId: string) => () => void;
};

const SelectDescription = styled(BoxSmallLabel)`
  width: 400px;
`;

const LayoutComplement = styled.span`
  display: inline-block;
  width: 370px;
  height: 10px;
  padding-left: 32px;
`;

const NewParticipantsForm = ({
  surveyGroup,
  selectedRespondents,
  setSelectedRespondents,
  organization,
  addedOn,
  setAddedOn,
  runOngoingSteps,
  onOngoingPhaseChange,
}: NewParticipantsFormProps) => {
  const { t, i18n } = useTranslation('surveyGroup');
  const { loading: respondentsLoading, data: respondentsData } = useActiveRespondentsQuery({ fetchPolicy: 'no-cache' });
  const { loading: sgRespondentsLoading, data: sgRespondentsData } = useSurveyGroupWithRespondentsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      surveyGroupId: surveyGroup.id,
    },
  });

  if (respondentsLoading || sgRespondentsLoading) {
    return <LoadingCard />;
  }

  const activeRespondents =
    respondentsData && respondentsData.user.organization ? respondentsData.user.organization.activeRespondents : [];
  const respondents = activeRespondents
    .filter((r) => selectedRespondents.some((sr) => sr.id === r.id))
    .map((r) => ({ ...r, type: selectedRespondents?.find((sr) => sr.id === r.id)?.type }));

  return (
    <>
      <BoxSmallLabel data-icom={'label-survey-group-added-on'}>{t('startDate')}</BoxSmallLabel>
      <DateSelectorInputGroup>
        <DateSelector>
          <DateInput
            onDateChange={(value) => setAddedOn(new Date(value.toISOString().substring(0, 10) + 'T00:00:00Z'))}
            inputName={'addedOn'}
            placeholder={t('selectDate')}
            date={addedOn?.toISOString()}
          />
        </DateSelector>
      </DateSelectorInputGroup>

      <SelectDescription className="mt-6" data-icom={'label-survey-group-respondents'}>
        {t('addRespondentsTitle')}
      </SelectDescription>
      <div className="d-flex mb-4" data-cy="admin-respondent-group" data-icom="admin-respondent-group-select">
        <TeamSelect
          setRespondents={(newRespondents) => {
            setSelectedRespondents((prevSelectedRespondents) =>
              newRespondents.map((respondent) => ({
                id: respondent.id,
                type:
                  prevSelectedRespondents.find((sr) => sr.id === respondent.id)?.type ??
                  SurveyGroupRespondentImportType.CreateNewRun,
              })),
            );
          }}
          teams={respondentsData?.user.organization?.teams}
          activeRespondents={activeRespondents}
          respondents={respondents}
        />
        <LayoutComplement />
      </div>

      <div className="mt-8">
        {selectedRespondents.length > 0 && (
          <SurveyRespondentsTable
            respondents={sortRespondents(respondents)}
            setRespondentRunType={(id, type) => {
              setSelectedRespondents((respondents) => respondents.map((r) => (r.id === id ? { ...r, type } : r)));
            }}
            existingRespondents={sgRespondentsData?.surveyGroup?.surveyGroupRespondents?.filter((sg) => !sg.deleted)}
            removeRespondent={(ids) => {
              setSelectedRespondents((respondents) => respondents.filter((rId) => !ids.includes(rId.id)));
            }}
          />
        )}
      </div>
      {selectedRespondents.length > 0 && addedOn && (
        <>
          <SurveyGroupSurveysPreviewTable
            topicGroup={surveyGroup.topicGroup}
            addedOn={addedOn}
            productHistory={organization?.productVersionHistory ?? []}
          />
          <div className="mt-8 mb-8">
            <HeadingSmall margin={`0 0 ${theme.spacing.f} 0`}>{t('addSurveyGroupRespondentModal:text')}</HeadingSmall>
            <BodyMedium margin={`0 0 ${theme.spacing.f} 0`}>
              {selectedRespondents && (
                <>
                  {t('addSurveyGroupRespondentModal:numberOfAdded')} <strong>{selectedRespondents?.length}</strong>{' '}
                  <br />
                </>
              )}
              {t('addSurveyGroupRespondentModal:addedOn')}{' '}
              <strong>{getLocalizedDateWithoutLeadingZeroes(addedOn!, i18n.language)}</strong>
            </BodyMedium>
            {getDataSource(surveyGroup.topicGroup, addedOn!, i18n.language, t, organization?.productVersionHistory)
              .filter((row) => row.rawStatus === SurveyStatus.Ongoing)
              .map((row) => {
                return (
                  <Checkbox
                    key={row.id}
                    selected={runOngoingSteps.includes(row.id)}
                    onToggle={onOngoingPhaseChange(row.id)}
                    text={
                      <BodyMedium>
                        {t('addSurveyGroupRespondentModal:ongoingPhase')} <strong>{row.topic}</strong>
                      </BodyMedium>
                    }
                  />
                );
              })}
          </div>
        </>
      )}
    </>
  );
};

export default NewParticipantsForm;
